import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

console.log('[API CONFIG] Backend URL:', BACKEND_URL);
console.log('[API CONFIG] API URL:', API);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    console.log('[API INTERCEPTOR] Response success:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('[API INTERCEPTOR] Response error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      code: error.code,
      data: error.response?.data
    });
    
    // Only redirect on 401 if we're NOT on the login endpoint
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      console.log('[API INTERCEPTOR] 401 detected on non-login endpoint, clearing session');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (mobile, password) => {
    console.log('[API] Login request - mobile:', mobile, 'password length:', password?.length);
    console.log('[API] API Base URL:', API);
    try {
      const response = await apiClient.post('/auth/login', { mobile, password });
      console.log('[API] Login response status:', response.status);
      console.log('[API] Login response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Login error:', error);
      console.error('[API] Login error response:', error.response);
      throw error;
    }
  },
  
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/user');
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async (role = null) => {
    const params = role ? { role } : {};
    const response = await apiClient.get('/users', { params });
    return response.data;
  },
  
  getAdminView: async () => {
    const response = await apiClient.get('/users/admin-view');
    return response.data;
  },
  
  getClients: async () => {
    const response = await apiClient.get('/users/clients');
    return response.data;
  },
  
  getOperators: async () => {
    const response = await apiClient.get('/users/operators');
    return response.data;
  },
  
  getCoordinators: async () => {
    const response = await apiClient.get('/users/coordinators');
    return response.data;
  },
  
  create: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  // Restricted staff-create: server rejects any role outside its allowlist and can
  // never create owner/admin. Used by the Accountant "Add User" flow.
  createStaff: async (userData) => {
    const response = await apiClient.post('/users/staff', userData);
    return response.data;
  },

  createClient: async (userData) => {
    const response = await apiClient.post('/users/client', userData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/users/${id}`, updateData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  }
};

// Projects API
export const projectsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/projects');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/projects/${id}`);
    return response.data;
  },
  
  create: async (projectData) => {
    const response = await apiClient.post('/projects', projectData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/projects/${id}`, updateData);
    return response.data;
  }
};

// Job Cards API
export const jobCardsAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/job-cards', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/job-cards/${id}`);
    return response.data;
  },
  
  getByType: async (cardType) => {
    const response = await apiClient.get('/job-cards', { params: { card_type: cardType } });
    return response.data;
  },
  
  create: async (jobCardData) => {
    const response = await apiClient.post('/job-cards', jobCardData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/job-cards/${id}`, updateData);
    return response.data;
  },
  
  close: async (id) => {
    const response = await apiClient.put(`/job-cards/${id}/close`);
    return response.data;
  },
  
  cancel: async (id, reason) => {
    const response = await apiClient.put(`/job-cards/${id}/cancel?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },
  
  reactivate: async (id) => {
    const response = await apiClient.put(`/job-cards/${id}/reactivate`);
    return response.data;
  },
  
  sendToAccounts: async (id, customLines = null) => {
    const response = await apiClient.put(`/job-cards/${id}/send-to-accounts`, customLines);
    return response.data;
  },
  
  sendToSupervisor: async (id, customLines = null) => {
    const response = await apiClient.put(`/job-cards/${id}/send-to-supervisor`, customLines);
    return response.data;
  },
  
  supervisorApprove: async (id) => {
    const response = await apiClient.put(`/job-cards/${id}/supervisor-approve`);
    return response.data;
  },
  
  // Read-only one-page bill: the confirmed lines while in review, the live
  // accounts estimate afterwards. No discount or payment amounts.
  getBill: async (id) => {
    const response = await apiClient.get(`/job-cards/${id}/bill`);
    return response.data;
  },

  reopenForProduction: async (id, reason) => {
    const response = await apiClient.put(`/job-cards/${id}/reopen-for-production?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  supervisorReject: async (id, reason) => {
    const response = await apiClient.put(`/job-cards/${id}/supervisor-reject?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  returnToCoordinator: async (id, reason) => {
    const response = await apiClient.put(`/job-cards/${id}/return-to-coordinator?reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  getEditLogs: async (id) => {
    const response = await apiClient.get(`/job-cards/${id}/edit-logs`);
    return response.data;
  },

  getByBucket: async (bucket) => {
    const response = await apiClient.get(`/job-cards-bucket/${bucket}`);
    return response.data;
  },

  getWorkLogs: async (id) => {
    const response = await apiClient.get('/operator-logs', { params: { job_card_id: id } });
    return response.data;
  },

  getEstimatePreview: async (id) => {
    const response = await apiClient.post(`/job-cards/${id}/estimate-preview`);
    return response.data;
  },
  
  getItemDescriptions: async () => {
    const response = await apiClient.get('/job-cards-item-descriptions');
    return response.data;
  },
  
  pushToProduction: async (id) => {
    const response = await apiClient.put(`/job-cards/${id}/push-to-production`);
    return response.data;
  },
  
  uploadAttachment: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(`/job-cards/${id}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadPressingImage: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/job-cards/${id}/pressing-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deletePressingImage: async (id, imageUrl) => {
    const response = await apiClient.delete(`/job-cards/${id}/pressing-images`, {
      params: { image_url: imageUrl }
    });
    return response.data;
  },

  deleteJobCard: async (id) => {
    const response = await apiClient.delete(`/job-cards/${id}`);
    return response.data;
  },
  
  // Export APIs
  exportPDF: async (id) => {
    const response = await apiClient.get(`/job-cards/${id}/export/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportExcel: async (id) => {
    const response = await apiClient.get(`/job-cards/${id}/export/excel`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // Operator status update APIs
  updatePressingLineStatus: async (jobCardId, lineType, lineId, status, operatorNote = null, machineType = null) => {
    const params = { line_type: lineType, line_id: lineId, status };
    if (operatorNote) params.operator_note = operatorNote;
    if (machineType) params.machine_type = machineType;
    const response = await apiClient.put(`/job-cards/${jobCardId}/pressing-line-status`, null, { params });
    return response.data;
  },
  
  updateCuttingStatus: async (jobCardId, status, operatorNote = null) => {
    const params = { status };
    if (operatorNote) params.operator_note = operatorNote;
    const response = await apiClient.put(`/job-cards/${jobCardId}/cutting-status`, null, { params });
    return response.data;
  },
  
  // Boring operator: 'completed' = Finish, 'started' = Reopen
  updateBoringStatus: async (jobCardId, status) => {
    const response = await apiClient.put(`/job-cards/${jobCardId}/boring-status`, null, { params: { status } });
    return response.data;
  },

  updateBoringQuantity: async (jobCardId, quantity, operatorNote = null) => {
    const params = { quantity };
    if (operatorNote) params.operator_note = operatorNote;
    const response = await apiClient.put(`/job-cards/${jobCardId}/boring-quantity`, null, { params });
    return response.data;
  },
  
  // Machine type/number updates for operators
  updatePressingMachineType: async (jobCardId, machineType) => {
    const response = await apiClient.put(`/job-cards/${jobCardId}/pressing-machine-type`, null, { 
      params: { machine_type: machineType } 
    });
    return response.data;
  },
  
  updateCuttingMachineNumber: async (jobCardId, machineNumber, cuttingQty = null, machineId = null, machineName = null) => {
    const params = { machine_number: machineNumber };
    if (cuttingQty !== null) params.cutting_qty = cuttingQty;
    if (machineId) params.machine_id = machineId;
    if (machineName) params.machine_name = machineName;
    const response = await apiClient.put(`/job-cards/${jobCardId}/cutting-machine-number`, null, { params });
    return response.data;
  }
};

// Operator Activities API
export const operatorActivitiesAPI = {
  log: async (activityData) => {
    const response = await apiClient.post('/operator-activities', activityData);
    return response.data;
  },
  
  getAll: async (params = {}) => {
    const response = await apiClient.get('/operator-activities', { params });
    return response.data;
  },
  
  getMy: async () => {
    const response = await apiClient.get('/operator-activities/my');
    return response.data;
  }
};

// Supervisor Notes API
export const supervisorNotesAPI = {
  create: async (noteData) => {
    const response = await apiClient.post('/supervisor-notes', noteData);
    return response.data;
  },
  
  getAll: async (params = {}) => {
    const response = await apiClient.get('/supervisor-notes', { params });
    return response.data;
  }
};

// Estimates API
export const estimatesAPI = {
  getAll: async () => {
    const response = await apiClient.get('/estimates');
    return response.data;
  },
  
  getClientInvoices: async () => {
    const response = await apiClient.get('/estimates/client');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/estimates/${id}`);
    return response.data;
  },
  
  create: async (estimateData) => {
    const response = await apiClient.post('/estimates', estimateData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/estimates/${id}`, updateData);
    return response.data;
  },
  
  sendToClient: async (id) => {
    const response = await apiClient.put(`/estimates/${id}/send-to-client`);
    return response.data;
  },

  setSettled: async (id, value) => {
    const response = await apiClient.put(`/estimates/${id}/settled?value=${value}`);
    return response.data;
  },
  
  markPaymentReceived: async (id, amount, discount, roundOff, discountReason) => {
    const params = new URLSearchParams();
    if (amount != null) params.append('amount', amount);
    if (discount != null && discount > 0) params.append('discount', discount);
    // Send the reason whenever a discount is applied — even an empty string, so a
    // Skip explicitly clears any old reason rather than silently keeping it.
    if (discount != null && discount > 0) params.append('discount_reason', discountReason || '');
    if (roundOff != null && roundOff > 0) params.append('round_off', roundOff);
    const qs = params.toString();
    const url = qs ? `/estimates/${id}/payment-received?${qs}` : `/estimates/${id}/payment-received`;
    const response = await apiClient.put(url);
    return response.data;
  },
  
  // Export APIs
  exportPDF: async (id) => {
    const response = await apiClient.get(`/estimates/${id}/export/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportExcel: async (id) => {
    const response = await apiClient.get(`/estimates/${id}/export/excel`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // WhatsApp Share API
  shareWhatsApp: async (id) => {
    const response = await apiClient.post(`/estimates/${id}/share/whatsapp`);
    return response.data;
  }
};

// Machines API
export const machinesAPI = {
  getAll: async (params = {}) => {
    // params: { category, active_only }
    const response = await apiClient.get('/machines', { params });
    return response.data;
  },

  create: async (payload) => {
    const response = await apiClient.post('/machines', payload);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/machines/${id}`, updateData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/machines/${id}`);
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await apiClient.get('/dashboard/stats');
    return response.data;
  }
};

// Config API (for predefined items)
export const configAPI = {
  get: async () => {
    const response = await apiClient.get('/config');
    return response.data;
  }
};

// Rate Chart API
export const rateChartAPI = {
  getAll: async (includeInactive = false, category = null) => {
    const params = { include_inactive: includeInactive };
    if (category) params.category = category;
    const response = await apiClient.get('/rate-chart', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/rate-chart/${id}`);
    return response.data;
  },
  
  create: async (itemData) => {
    const response = await apiClient.post('/rate-chart', itemData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/rate-chart/${id}`, updateData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/rate-chart/${id}`);
    return response.data;
  },
  
  lookupRate: async (description) => {
    const response = await apiClient.get(`/rate-chart/lookup/${encodeURIComponent(description)}`);
    return response.data;
  }
};

// Bill of Materials (BOM) API
export const bomAPI = {
  getAll: async (projectId = null) => {
    const params = projectId ? { project_id: projectId } : {};
    const response = await apiClient.get('/boms', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/boms/${id}`);
    return response.data;
  },
  
  create: async (bomData) => {
    const response = await apiClient.post('/boms', bomData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/boms/${id}`, updateData);
    return response.data;
  },
  
  addMaterials: async (id, materials) => {
    const response = await apiClient.post(`/boms/${id}/materials`, materials);
    return response.data;
  },
  
  sendToClient: async (id) => {
    const response = await apiClient.put(`/boms/${id}/send`);
    return response.data;
  },
  
  markReceived: async (id) => {
    const response = await apiClient.put(`/boms/${id}/received`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/boms/${id}`);
    return response.data;
  },
  
  // Get received BOMs (for Supervisor)
  getReceived: async () => {
    const response = await apiClient.get('/boms/received');
    return response.data;
  },
  
  // Upload invoice photo (Supervisor only)
  uploadInvoice: async (bomId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/boms/${bomId}/invoice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteInvoicePhoto: async (bomId, imageUrl) => {
    const response = await apiClient.delete(`/boms/${bomId}/invoice`, {
      params: { image_url: imageUrl }
    });
    return response.data;
  },
  
  // Export APIs
  exportPDF: async (id) => {
    const response = await apiClient.get(`/boms/${id}/export/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  exportExcel: async (id) => {
    const response = await apiClient.get(`/boms/${id}/export/excel`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  // WhatsApp Share API
  shareWhatsApp: async (id) => {
    const response = await apiClient.post(`/boms/${id}/share/whatsapp`);
    return response.data;
  }
};

// Coordinator Interns API
export const internsAPI = {
  getAll: async (includeInactive = false) => {
    const response = await apiClient.get('/interns', { params: { include_inactive: includeInactive } });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/interns/${id}`);
    return response.data;
  },
  
  create: async (internData) => {
    const response = await apiClient.post('/interns', internData);
    return response.data;
  },
  
  update: async (id, updateData) => {
    const response = await apiClient.put(`/interns/${id}`, updateData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/interns/${id}`);
    return response.data;
  }
};

// Coordinator Tasks API
export const coordinatorTasksAPI = {
  updateTask: async (jobCardId, taskType, performedBy, performedByRole) => {
    const response = await apiClient.put(`/job-cards/${jobCardId}/tasks/${taskType}`, {
      performed_by: performedBy,
      performed_by_role: performedByRole
    });
    return response.data;
  },
  
  resetTask: async (jobCardId, taskType) => {
    const response = await apiClient.delete(`/job-cards/${jobCardId}/tasks/${taskType}`);
    return response.data;
  }
};

// Operator Work Logs API
export const operatorLogsAPI = {
  create: async (logData) => {
    const response = await apiClient.post('/operator-logs', logData);
    return response.data;
  },
  
  complete: async (logId) => {
    const response = await apiClient.put(`/operator-logs/${logId}/complete`);
    return response.data;
  },
  
  getAll: async (operatorId = null, jobCardId = null) => {
    const params = {};
    if (operatorId) params.operator_id = operatorId;
    if (jobCardId) params.job_card_id = jobCardId;
    const response = await apiClient.get('/operator-logs', { params });
    return response.data;
  }
};

// Performance Reports API
export const reportsAPI = {
  // F5: logins and last-seen per account (owner/admin)
  getUsage: async (days = 30, userId = null) => {
    const params = { days };
    if (userId) params.user_id = userId;
    const response = await apiClient.get('/reports/usage', { params });
    return response.data;
  },

  getOperatorPerformance: async (period = 'current', operatorId = null, startDate = null, endDate = null) => {
    const params = { period };
    if (operatorId) params.operator_id = operatorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/operators', { params });
    return response.data;
  },
  
  getCoordinatorPerformance: async (period = 'current', coordinatorId = null, startDate = null, endDate = null) => {
    const params = { period };
    if (coordinatorId) params.coordinator_id = coordinatorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/coordinators', { params });
    return response.data;
  },
  
  getJobCardOperatorStats: async (jobCardId) => {
    const response = await apiClient.get(`/reports/job-card/${jobCardId}/operators`);
    return response.data;
  },

  getCuttingByMachine: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/cutting-by-machine', { params });
    return response.data;
  },

  getPressingByMachineType: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/pressing-by-machine-type', { params });
    return response.data;
  },

  getCuttingMachineHeatmap: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/cutting-machine-heatmap', { params });
    return response.data;
  },

  getCoordinatorTasks: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/coordinator-tasks', { params });
    return response.data;
  },

  getProductionTrends: async (granularity = 'day', period = 'monthly', startDate = null, endDate = null) => {
    const params = { granularity, period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/production-trends', { params });
    return response.data;
  },

  getClients: async () => {
    const response = await apiClient.get('/reports/clients');
    return response.data;
  },

  getReceivables: async () => {
    const response = await apiClient.get('/reports/receivables');
    return response.data;
  },

  getSupervisors: async (period = 'monthly', startDate = null, endDate = null) => {
    const params = { period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await apiClient.get('/reports/supervisors', { params });
    return response.data;
  }
};

// Edit Logs API
export const editLogsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/edit-logs');
    return response.data;
  },
  
  getByEstimate: async (estimateId) => {
    const response = await apiClient.get('/edit-logs', { params: { estimate_id: estimateId } });
    return response.data;
  }
};

export const notificationsAPI = {
  getAll: async (unreadOnly = false) => {
    const response = await apiClient.get('/notifications', { params: { unread_only: unreadOnly } });
    return response.data;
  },
  markRead: async (notificationId) => {
    const response = await apiClient.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await apiClient.put('/notifications/read-all');
    return response.data;
  },
  getPreferences: async () => {
    const response = await apiClient.get('/notifications/preferences');
    return response.data;
  },
  updatePreferences: async (prefs) => {
    const response = await apiClient.put('/notifications/preferences', prefs);
    return response.data;
  }
};

export default apiClient;
